import React, { useEffect, useState } from 'react';
import Heading from '../../../../../common/element/Heading';
import { setBreadcrumbs } from '../../../../../redux/Admin/breadcrumbsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { getSalesTrainingCompanyById } from '../../../../../redux/SalesTrainingCompanies/SalesTrainingCompaniesslice';
import { setSalesTrainingName, resetSalesTrainingName } from '../../../../../redux/Admin/salesTrainingViewSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import OverView from './OverView';
import SessionMaster from './SalesTrainingSession';
import SalesTrainingSessionView from './SalesTrainingSession/SalesTrainingSessionView';
import { CompanyAvatar } from '../../../../../common/element/CompanyAvatar/CompanyAvatar';
import ImagePreviewModal from '../../../../../common/ImagePreviewModal/ImagePreviewModal';

const ViewSalesTrainingCompany = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const idFromQuery = query.get('id');
  const tabFromQuery = query.get('tab') || 'overview';
  const sessionView = query.get('sessionView');
  const [activeTab, setActiveTab] = useState(tabFromQuery);
  const [imageModal, setImageModal] = useState({ isOpen: false, url: "", alt: "" });

  const dispatch = useDispatch();

  const salesTrainingId = idFromQuery ? parseInt(idFromQuery, 10) : null;
  const { data: detail, loading } = useSelector((state) => state.salesTrainingCompanies.getSalesTrainingCompanyById);
  const salesTrainingName = useSelector((state) => state.salesTrainingView?.salesTrainingName);

  useEffect(() => {
    const label = salesTrainingName || 'View Sales Training Company';
    dispatch(
      setBreadcrumbs([
        { label: 'Sales Training Companies', url: '/salesTrainingCompanies', home: '/companies' },
        { label: label, url: `/salesTrainingCompanies/view?id=${salesTrainingId}&tab=${tabFromQuery}` },
      ])
    );
  }, [dispatch, salesTrainingName, salesTrainingId]);

  useEffect(() => {
    if (salesTrainingId) {
      dispatch(getSalesTrainingCompanyById(salesTrainingId));
    }
  }, [dispatch, salesTrainingId]);

  useEffect(() => {
    if (detail?.user) {
      const fullName = `${detail.user.first_name || ''} ${detail.user.last_name || ''}`.trim();
      if (fullName) dispatch(setSalesTrainingName(fullName));
    }
  }, [dispatch, detail]);

  useEffect(() => {
    return () => { dispatch(resetSalesTrainingName()); };
  }, [dispatch]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  useEffect(() => {
    const currentTab = query.get('tab') || 'overview';
    if (currentTab !== activeTab) setActiveTab(currentTab);
  }, [location.search]);

  const openImagePreview = () => {
    if (!detail?.user?.profile_image) return;
    setImageModal({ isOpen: true, url: detail.user.profile_image, alt: `${detail.user.first_name || ''} ${detail.user.last_name || ''}`.trim() || "Profile Image" });
  };

  const closeImagePreview = () => {
    setImageModal({ isOpen: false, url: "", alt: "" });
  };

  return (
    <div>
      <div className="flex justify-between mb-5">
        <div className="w-full">
          {sessionView === "view" ? "" : (
            <>
              <div className="flex items-center gap-4 mb-6">
                <div
                  onClick={openImagePreview}
                  className={detail?.user?.profile_image ? "cursor-pointer hover:opacity-80 transition" : ""}
                  title={detail?.user?.profile_image ? "View Profile Image" : ""}
                >
                  <CompanyAvatar logo={detail?.user?.profile_image} name={`${detail?.user?.first_name || ''} ${detail?.user?.last_name || ''}`} />
                </div>

                <div>
                  <Heading>
                    {(detail?.user && `${detail.user.first_name} ${detail.user.last_name}`) || '-'}
                  </Heading>
                  <div className="flex items-center gap-3 mt-1">
                    <div className={`flex items-center gap-1 px-3 py-0.5 ${detail?.user?.isActive ? 'bg-[#ECFDF3]' : 'bg-[#FEF3F2]'} rounded-[16px]`}>
                      <div className={`w-2 h-2 ${detail?.user?.isActive ? 'bg-green-500' : 'bg-red-600'} rounded-full`}></div>
                      <span className={`font-lato font-medium text-[14px] ${detail?.user?.isActive ? 'text-[#027A48]' : 'text-[#B42318]'}`}>
                        {detail?.user?.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <span className="font-lato font-medium text-[14px] text-[#1D2939] flex items-center gap-2">
                      <span>{detail?.user?.email}</span> • <span>{detail?.user?.mobile_number}</span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-0 p-1 bg-[#FFF4E5] border border-[#FFF4E5] rounded-lg mb-6">
                {['overview', 'sessions'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    className={`flex-1 text-sm font-normal transition-colors py-2 px-3 rounded-md ${activeTab === tab
                      ? 'text-gray-900 bg-white shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)]'
                      : 'text-gray-600 bg-[#FFF4E5]'
                      }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </>
          )}

          {activeTab === 'overview' && (
            <OverView data={detail} loading={loading} />
          )}

          {activeTab === 'sessions' && (
            <>
              {sessionView === "view" ? <SalesTrainingSessionView /> : <SessionMaster />}
            </>
          )}
        </div>
      </div>

      <ImagePreviewModal
        isOpen={imageModal.isOpen}
        onClose={closeImagePreview}
        imageUrl={imageModal.url}
        altText={imageModal.alt}
      />
    </div>
  );
};

export default ViewSalesTrainingCompany;
